import { Box, SystemIcon, Typography } from '@wishket/design-system';
import { useParams, usePathname } from 'next/navigation';

import { useThrottle } from '@wishket/yogokit';

import { SelectionInfo } from '../hooks';

import { Link } from '@/shared/Components';
import { useDebounce, useHighlight, useProfile } from '@/shared/hooks';
import { PATH } from '@/shared/constants';
import { useAddHighlight } from '@/entities/article';

interface SelectionPopoverContentProps {
  selectionInfo?: SelectionInfo | null;
  popoverInit: () => void;
}

const MIN_LENGTH = 3;
const MAX_LENGTH = 250;

const SelectionPopoverContent = ({
  selectionInfo,
  popoverInit,
}: SelectionPopoverContentProps) => {
  const params = useParams();
  const pathname = usePathname();
  const { profile } = useProfile();
  const { mutate, isPending } = useAddHighlight();
  const { setId: setHighlightId } = useHighlight();

  const newsId = Number(params.id);
  const profileId = profile?.yozmitProfileId || 0;
  const hasProfile = !!profile;

  const validateSelection = (length: number) => {
    if (length < MIN_LENGTH) {
      alert('형광펜은 최소 3글자 이상부터 가능합니다.');
      return false;
    }
    if (length > MAX_LENGTH) {
      alert('형광펜은 최대 250글자까지 가능합니다.');
      return false;
    }
    return true;
  };

  const handleHighlight = (withMemo = false) => {
    if (!selectionInfo) return;
    if (!validateSelection(selectionInfo.pureTextLength)) return;
    if (isPending) return;

    const highlightData = {
      newsId,
      profileId,
      selectedString: selectionInfo.text,
      blockPosition: selectionInfo.startTagIndex,
      textPosition: selectionInfo.startOffset,
      selectedLength: selectionInfo.selectedLength,
    };

    mutate(highlightData, {
      onSuccess: (data) => {
        popoverInit();
      },
      onSettled: (data, error) => {
        if (error) return;
        if (!withMemo) return;

        setHighlightId(Number(data));
      },
    });
  };

  const debouncedAddHighlight = useThrottle(() => handleHighlight(false), 1000);
  const debouncedAddMemo = useDebounce(() => handleHighlight(true), 1000);

  return (
    <Box
      as="span"
      className="flex shrink-0 select-none items-center gap-3 px-4 py-2"
    >
      {hasProfile ? (
        <>
          <button
            type="button"
            className="group flex shrink-0 cursor-pointer items-center gap-1.5"
            onClick={debouncedAddHighlight}
          >
            <SystemIcon
              name="medium_highlight"
              className="group-hover:text-w-gray-600"
            />
            <Typography
              variant="body1"
              className="font-normal text-w-gray-900 group-hover:text-w-gray-600"
            >
              형광펜
            </Typography>
          </button>
          <Box as="span" className="h-4 w-px flex-shrink-0 bg-w-gray-200" />
          <button
            type="button"
            className="group flex shrink-0 cursor-pointer items-center gap-1.5"
            onClick={debouncedAddMemo}
          >
            <SystemIcon
              name="medium_bubble"
              className="group-hover:text-w-gray-600"
            />
            <Typography
              variant="body1"
              className="font-normal text-w-gray-900 group-hover:text-w-gray-600"
            >
              메모
            </Typography>
          </button>
        </>
      ) : (
        <Box as="span" className="typo-body1">
          <Link
            href={`${PATH.AUTH_LOGIN}?next=${pathname}`}
            className="font-medium text-primary desktop:hover:underline"
          >
            회원가입
          </Link>
          <Typography variant="body1" className="font-normal">
            {' 후 형광펜을 사용해보세요.'}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SelectionPopoverContent;
